<template>
    <div class>
        <ModalBox v-model="legacyModalVisible" size="md" hide-close header-class="!hidden" z-index="z-[60]" wrapper-classes="lg:h-auto lg:max-h-none p-6">

            <div class="flex flex-col gap-y-4">
                <!-- Header -->
                <div class="flex items-center gap-2">
                    <FormButton @click="dismiss" title="Back"
                        custom-classes="shrink-0 inline-flex size-8 items-center justify-center rounded-lg text-surface-8 hover:text-white hover:bg-surface-3 transition-colors">
                        <icon-base fill="none">
                            <icon-chevron-left />
                        </icon-base>
                    </FormButton>
                    <div class="min-w-0">
                        <p class="text-lg font-semibold text-white mb-0 leading-tight">Manage access</p>
                        <p class="text-tiny text-surface-7 mb-0 leading-tight mt-0.5">Control who can use your invite link
                            to add destinations.</p>
                    </div>
                </div>

                <!-- Add emails -->
                <div>
                    <FormInput type="email" v-model="emailInput" @keydown.enter.prevent="addEmails"
                        placeholder="user@example.com, user@example.com" />
                    <p class="text-tiny mt-1.5 text-surface-6 mb-0">Separate emails with a comma and press Enter to add them.
                    </p>
                    <p v-if="maxEmails" class="text-tiny mt-1 text-gold mb-0">You can add up to {{ maxGuestCount || 3 }}
                        emails.</p>
                </div>

                <!-- Loading -->
                <div v-if="processing" class="flex justify-center py-6" aria-busy="true">
                    <Spinner classes="text-dark-8" spinner-color="var(--c-dark-8)" spinner-size="22px" />
                </div>

                <!-- List -->
                <div v-else-if="emails.length > 0">
                    <p class="text-tiny font-medium text-surface-8 mb-2">Email addresses added</p>
                    <ul class="bg-surface-1 rounded-xl p-2 flex flex-col gap-y-1 max-h-[260px] overflow-y-auto">
                        <li v-for="(email, index) in emails" :key="index"
                            class="group flex items-center justify-between gap-3 px-3 h-10 rounded-lg hover:bg-surface-2 transition-colors">
                            <span class="text-tiny text-white truncate">{{ email }}</span>
                            <FormButton @click="removeEmail(index)" title="Remove"
                                custom-classes="shrink-0 text-surface-7 hover:text-error transition-colors opacity-0 group-hover:opacity-100 focus-visible:opacity-100">
                                <icon-base fill="none">
                                    <icon-bin />
                                </icon-base>
                            </FormButton>
                        </li>
                    </ul>
                </div>

                <!-- Empty -->
                <div v-else class="text-center py-6">
                    <p class="text-tiny text-surface-6 mb-0">No email addresses added yet. Add one above to control access.
                    </p>
                </div>

                <!-- Footer -->
                <div class="flex justify-end">
                    <FormButton type="secondary" size="sm" @click="dismiss">
                        Close
                    </FormButton>
                </div>
            </div>
        
</ModalBox>
    </div>
</template>

<script>
import ModalBox from '@/components/modals/ModalBox.vue'
import { legacyModalBridgeMixin } from '@/composables/useLegacyModalBridge'
import _ from "lodash";
import ClipLoader from "@/components/ui/ClipLoader.vue";
import FormButton from "../Atoms/FormButton.vue";
import FormInput from "../Atoms/FormInput.vue";
import IconCopyLink from "../icon/IconCopyLink.vue";
import IconBase from '@/components/icon/IconBase.vue'
import IconBin from '../icon/IconBin.vue';
import IconChevronLeft from "../icon/IconChevronLeft.vue";
import GuestDestinationService from "@/services/GuestDestinationService";
import Spinner from "../ui/Spinner.vue";

export default {
    name: "ManageAccessList",
    mixins: [legacyModalBridgeMixin],
    legacyModalId: 'modal-manage-access',
    components: {
		ModalBox,
        ClipLoader,
        FormButton,
        FormInput,
        IconCopyLink,
        IconBase,
        IconBin,
        IconChevronLeft,
        Spinner,
    },
    props: {
        streamId: {
            type: String
        },
        modalSize: {
            type: String,
            default: "sm",
            validator(v) {
                return ["xl", "lg", "md", "sm"].some((size) => size === v);
            },
        },
    },
    data() {
        return {
            maxEmails: '',
            emailInput: '',
            emails: [],
            processing: true,
            userSubscription: null,
            showcase: null,
            categoryNameMaps: {
                live: "Live Stream",
                restream: "Multistream",
                ipcam: "IP Camera",
                vod: "Video Hosting",
            },
            categoryCollapseHash: {
                restream: true,
                live: true,
                vod: true,
                ipcam: true,
            },
            selectedStream: [],
            selectedVod: [],
            videoFiles: [],
            selectedVodFiles: [],
            showVideofiles: false,
            livetovod: false,
            setOrder: [],
            liveCount: null,
            vodCount: null,
            liveStreamSearchKey: "",
        };
    },
    watch: {
        selectedStream: function () {
            let livestream = this.selectedStream.filter((s) => {
                return s.type;
            });
            this.vodCount =
                this.selectedStream.length - livestream.length > 0
                    ? this.selectedStream.length - livestream.length
                    : null;
            this.liveCount = livestream.length;
        },
    },
    computed: {
        link() {
            return `${location.origin}/app/invite-link/${this.$props.streamId}`
        },
        maxGuestCount() {
            const liveSub = _.find(this.$store.state.User.addonSub, { category: 'live', enabled: true });
            if(!liveSub) return 3;
            if('maxGuestsPerStream' in liveSub.definitionOverride) {
                return liveSub.definitionOverride.maxGuestsPerStream;
            }
            return liveSub?.package?.definition?.maxGuestsPerStream;
        },
    },

    async mounted() { },

    methods: {
        onLegacyModalShow() {
            this.updateShowcase();
        },
        async addEmails() {
            const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

            this.loadingEmails = true;
            // console.log('loadingEmails: true');

            const newEmails = this.emailInput
                .split(',')
                .map((e) => e.trim())
                .filter((e) => e)
            
            const invalidEmail = newEmails.find((email) => !emailPattern.test(email))
            if (invalidEmail) {
                this.$notify({group: 'error', text: 'Please enter a valid email.'})
                return;
            }

            const duplicateEmail = newEmails.find((email) => this.emails.includes(email));
            if (duplicateEmail) {
                this.$notify({ group: 'error', text: 'This email is already added.'})
                return;
            }

            const availableSlots = (this.maxGuestCount || 3) - this.emails.length;
            if(availableSlots <= 0) {
                this.maxEmails = true
                this.$notify({ group: 'error', text: `Only ${(this.maxGuestCount || 3)} email(s) can be added.`})
                this.emailInput = ''
                return
            }

            const emailsToAdd = newEmails.slice(0, availableSlots)

            this.emails.push(...emailsToAdd);
            this.$emit('saved-emails', this.emails)
            this.maxEmails = newEmails.length > availableSlots
            this.emailInput = '';
            await this.$nextTick();
            this.loadingEmails = false;

            this.updateManageAccessList();
        },
        async updateManageAccessList() {
            try {
                await GuestDestinationService.updateAccessList(this.$props.streamId, this.emails);
			    this.$notify({ group: 'success', text: 'Successfully updated the access list' });
            } catch(e) {
                console.log(e);
			    this.$notify({ group: 'error', text: 'Error when updating the access list' });
            }
        },
        async fetchAccessList() {
            this.emails = [];
            // console.log(this.$props.streamId)
            this.emails = await GuestDestinationService.getAccessList(this.$props.streamId);
        },
        removeEmail(index) {
            const deletedEmail = this.emails.splice(index, 1)
            if(deletedEmail.length) {
                this.updateManageAccessList();
                this.$emit('remove-guest-destinations', deletedEmail[0]);
            }
        },
        copyToClipboard() {
            navigator.clipboard.writeText(this.link);
			this.$notify({ group: 'info', text: 'Copied to clipboard' });
        },
        async updateShowcase() {
            this.processing = true;
            await this.fetchAccessList();
            this.processing = false;
        },
        dismiss() {
            this.hideLegacyModal();
        },
    },
};
</script>

<style>
#select-guest-destinations-modal .modal-sm {
    max-width: 700px;
    padding: 0px 10px;
}

#select-guest-destinations-modal .modal-body .col {
    max-height: 80vh;
    overflow-y: auto;
}

#select-guest-destinations-modal .modal-sm .grid-item {
    width: 600px;
    max-width: 100%;
    flex: 0 0 100%;
}

#select-guest-destinations-modal .platform_details_wrapper {
    display: none;
}

#select-guest-destinations-modal_BV_modalbody ul {
    border-bottom: 1px solid var(--c-dark-3);
}

#select-guest-destinations-modal_BV_modalbody ul li a {
    background: transparent;
    border: 0px;
    color: var(--c-dark-8);
    font-weight: 400;
    font-size: 15px;
}

div#select-guest-destinations-modal___BV_modalbody ul li a.active {
    font-weight: 500;
    color: #fff;
    border-bottom: 2px solid #fff;
}
</style>


<style scroped>
.seach-icon {
    transform: translateX(0);
    background: transparent;
    border: none;
    color: rgba(173, 184, 220, 0.5);
}

.no-data-err {
    height: 25vh;
    display: flex;
    justify-content: center;
    align-items: center;

    text-align: center;
}

.no-data-err h3 {
    color: #fff;
    font-size: var(--font-s-s);
    font-weight: 500;
}

.no-data-err .err-description {
    color: rgba(135, 148, 181, 1);
    font-size: 13px;
    font-weight: 400;
}

@media only screen and (max-width: 600px) {
    .tab-vod .inner_folder_wrap>p:last-child {
        display: none !important;
    }

    .tab-vod .inner_folder_wrap>p {
        width: 50%;
    }

    .tab-livestream .inner_folder_wrap>div {
        width: 100%;
    }

    .files_parent_al {
        height: unset;
        padding: 10px 0px;
    }

    .files_parent_al .al_badge+span {
        padding-left: 38px;
    }

    #select-guest-destinations-modal .loading-showcase {
        padding: 0;
    }

    #select-guest-destinations-modal .modal-body {
        padding-left: 15px;
        padding-right: 15px;
    }

    #select-guest-destinations-modal .modal-body .col {
        overflow-x: hidden;
    }

    .tab-vod .files_parent_al>img:first-child {
        margin-right: 5px !important;
    }

    #select-guest-destinations-modal .sw_modal_action {
        padding: 0;
    }

    #select-guest-destinations-modal .sw_modal_action .btn-outline-danger {
        margin-right: 0 !important;
    }

    #select-guest-destinations-modal .sw_modal_action button {
        margin-bottom: 10px;
        width: 48%;
    }
}
</style>
